import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Camera, MapPin, Users, Share2, Wrench, Trophy, Sparkles, ArrowRight,
} from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="p-6 rounded-2xl bg-white/70 border border-slate-200 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow">
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

export default function LandingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const features = [
    { icon: MapPin,   key: 'f1' },
    { icon: Users,    key: 'f2' },
    { icon: Share2,   key: 'f3' },
    { icon: Wrench,   key: 'f4' },
    { icon: Trophy,   key: 'f5' },
    { icon: Sparkles, key: 'f6' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Nav */}
      <nav className="px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-900">PhotoPlan</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/pricing" className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">
            {t('landing.navPricing')}
          </Link>
          <LanguageSwitcher />
          {user ? (
            <Link to="/dashboard">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900 px-3 py-1.5">
                {t('landing.navSignIn')}
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {t('landing.ctaSignUp')}
                </Button>
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 pt-12 pb-20 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium mb-6">
          <Sparkles className="w-3 h-3" />
          {t('landing.hero.trustBadge')}
        </div>
        <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6">
          {t('landing.hero.tagline')}
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
          {t('landing.hero.subhead')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to={user ? '/dashboard' : '/register'}>
            <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 h-12">
              {t('landing.ctaSignUpAlt')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/pricing">
            <Button variant="ghost" size="lg" className="h-12 text-slate-700">
              {t('landing.pricingTeaser.seePlans')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-16 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
          {t('landing.features.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map(({ icon, key }) => (
            <FeatureCard
              key={key}
              icon={icon}
              title={t(`landing.features.${key}Title`)}
              desc={t(`landing.features.${key}Desc`)}
            />
          ))}
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="px-6 py-16 max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-slate-900 mb-3">
          {t('landing.pricingTeaser.title')}
        </h2>
        <p className="text-slate-600 mb-6">{t('landing.pricingTeaser.subtitle')}</p>
        <Link to="/pricing">
          <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50">
            {t('landing.pricingTeaser.seePlans')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-slate-200 mt-10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-emerald-600" />
            <span className="font-semibold text-slate-900">PhotoPlan</span>
            <span className="text-sm text-slate-500">— {t('landing.footer.tagline')}</span>
          </div>
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} PhotoPlan. {t('landing.footer.rights')}
          </p>
        </div>
      </footer>
    </div>
  );
}
